import { handleActions } from 'redux-actions';
import { defaultDeactivateAccountWidgetState } from './defaults';
import {
  DeactivateAccountWidgetData,
  DeactivateAccountWidgetStep,
} from './types';
import {
  initDeactivationConfirmationPage,
  initDeactivationPage,
  initDeactivationStatus,
} from '../../actions';
import {
  setDeactivateAccountWidgetStep,
  submitConfirmDeactivationModalSuccess,
  submitConfirmDeactivationModalFailure,
  submitConfirmDeactivationModal,
  submitSendVerificationEmailModalSuccess,
  submitSendVerificationEmailModal,
  submitReactivateFailure,
  submitReactivateSuccess,
  submitReactivate,
} from './actions';
import { NetworkRequestState } from '../baseTypes';

function determineCurrentStepForNormalInit(
  initAction: ReturnType<typeof initDeactivationPage>
) {
  const { canReactivate } = initAction.payload.data;
  if (canReactivate) {
    return DeactivateAccountWidgetStep.Reactivate;
  } else {
    return DeactivateAccountWidgetStep.Initial;
  }
}

export default handleActions<DeactivateAccountWidgetData, any>(
  {
    [initDeactivationPage.toString()]: (
      state,
      action: ReturnType<typeof initDeactivationPage>
    ) => {
      return {
        ...state,
        step: determineCurrentStepForNormalInit(action),
        canReactivate: action.payload.data.canReactivate,
        isDeactivated: action.payload.data.isDeactivated,
        timeUntilReactivation: action.payload.data.timeUntilReactivation,
        timeUntilReactivationEnds:
          action.payload.data.timeUntilReactivationEnds,
        linkSource: action.payload.data.linkSource,
        email: action.payload.data.email,
      };
    },
    [initDeactivationConfirmationPage.toString()]: (
      state,
      action: ReturnType<typeof initDeactivationConfirmationPage>
    ) => {
      return {
        ...state,
        step: DeactivateAccountWidgetStep.VerificationSuccess,
        linkSource: action.payload.data.emailLinkSource,
        confirmation: {
          emailToken: action.payload.data.emailToken,
          emailTokenTs: action.payload.data.emailTokenTs,
          presetReasons: action.payload.data.presetReasons,
        },
      };
    },
    [initDeactivationStatus.toString()]: (
      state,
      action: ReturnType<typeof initDeactivationStatus>
    ) => {
      return {
        ...state,
        step: action.payload.status.canReactivate
          ? DeactivateAccountWidgetStep.Reactivate
          : DeactivateAccountWidgetStep.Initial,
      };
    },
    [submitSendVerificationEmailModal.toString()]: (
      state,
      action: ReturnType<typeof submitSendVerificationEmailModal>
    ) => {
      return {
        ...state,
        request: { status: NetworkRequestState.SENT_AND_WAITING_FOR_RESPONSE },
      };
    },
    [submitSendVerificationEmailModalSuccess.toString()]: (
      state,
      action: ReturnType<typeof submitSendVerificationEmailModalSuccess>
    ) => {
      return {
        ...state,
        step: DeactivateAccountWidgetStep.VerificationEmailSent,
        request: { status: NetworkRequestState.IDLE },
      };
    },
    [submitConfirmDeactivationModal.toString()]: (
      state,
      action: ReturnType<typeof submitConfirmDeactivationModal>
    ) => {
      return {
        ...state,
        request: { status: NetworkRequestState.SENT_AND_WAITING_FOR_RESPONSE },
      };
    },
    [submitConfirmDeactivationModalSuccess.toString()]: (
      state,
      action: ReturnType<typeof submitConfirmDeactivationModalSuccess>
    ) => {
      return {
        ...state,
        request: { status: NetworkRequestState.SUCCESS },
      };
    },
    [submitConfirmDeactivationModalFailure.toString()]: (
      state,
      action: ReturnType<typeof submitConfirmDeactivationModalFailure>
    ) => {
      return {
        ...state,
        request: {
          status: NetworkRequestState.FAILURE,
          errorMessage: action.payload.error,
        },
      };
    },
    [submitReactivate.toString()]: (
      state,
      action: ReturnType<typeof submitReactivate>
    ) => {
      return {
        ...state,
        request: { status: NetworkRequestState.SENT_AND_WAITING_FOR_RESPONSE },
      };
    },
    [submitReactivateSuccess.toString()]: (
      state,
      action: ReturnType<typeof submitReactivateSuccess>
    ) => {
      return {
        ...state,
        step: DeactivateAccountWidgetStep.Reactivate,
        request: { status: NetworkRequestState.SUCCESS },
      };
    },
    [submitReactivateFailure.toString()]: (
      state,
      action: ReturnType<typeof submitReactivateFailure>
    ) => {
      return {
        ...state,
        request: {
          status: NetworkRequestState.FAILURE,
          errorMessage: action.payload.error,
        },
      };
    },
    [setDeactivateAccountWidgetStep.toString()]: (
      state,
      action: ReturnType<typeof setDeactivateAccountWidgetStep>
    ) => ({
      ...state,
      step: action.payload.step,
    }),
  },
  defaultDeactivateAccountWidgetState
);
